<script lang="ts" setup>
import {
  renderActivityTitle,
  formatActivityTimestamp,
  activityIconClass,
  activityColorClass,
} from "@/queries/activities";
import type { PartnerActivityRow } from "@/types";
import OrganizationSwitchModal from "@/views/shared/components/modals/OrganizationSwitchModal.vue";
import { useActivityOrgSwitch } from "@/composables/useActivityOrgSwitch";

defineProps<{
  activities: PartnerActivityRow[];
  loading?: boolean;
}>();

const orgSwitch = useActivityOrgSwitch();
</script>

<template>
  <div class="partner-activity">
    <div v-if="loading" class="text-center py-4">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <div v-else-if="activities.length === 0" class="text-center py-4 text-muted">
      <i class="bi bi-activity fs-1 mb-2"></i>
      <p>No recent activity</p>
    </div>

    <div v-else class="activity-list p-0">
      <div
        v-for="activity in activities"
        :key="activity.id"
        class="activity-item d-flex align-items-start gap-3 py-3"
        @click="orgSwitch.open(activity)"
        role="button"
        tabindex="0"
        @keydown.enter="orgSwitch.open(activity)"
        @keydown.space.prevent="orgSwitch.open(activity)"
      >
        <div class="activity-icon">
          <i
            :class="[activityIconClass(activity), activityColorClass(activity.severity)]"
            class="fs-5"
          ></i>
        </div>

        <div class="activity-content flex-grow-1">
          <div class="d-flex justify-content-between align-items-start">
            <h3 class="activity-title mb-1 h4">{{ renderActivityTitle(activity) }}</h3>
            <span class="activity-time text-muted small">
              {{ formatActivityTimestamp(activity.occurredOn) }}
            </span>
          </div>

          <div class="activity-meta small">
            <span class="badge bg-secondary me-2">{{ activity.clientOrgName }}</span>
            <span class="text-muted">{{ activity.category }}</span>
          </div>
        </div>
      </div>
    </div>

    <div v-if="activities.length > 0" class="text-center pt-3 border-top">
      <router-link to="/partners/activity" class="bb-btn-secondary-cta btn-sm">
        <i class="bi bi-arrow-right me-1"></i>View All Activity
      </router-link>
    </div>
  </div>

  <OrganizationSwitchModal
    v-bind="orgSwitch.modalProps.value"
    @update:show="orgSwitch.onShowChange"
    @cancelled="orgSwitch.onCancelled"
  />
</template>

<style scoped lang="scss">
.partner-activity {
  max-height: 500px;
  overflow-y: auto;
}

.activity-item {
  border-bottom: 1px solid var(--bs-border-color);
  transition: background-color 0.2s ease;
  cursor: pointer;
}

.activity-item:hover {
  background-color: var(--bs-secondary-bg);
  border-radius: 0.375rem;
  padding-left: 1rem;
  padding-right: 1rem;
}

.activity-item:last-child {
  border-bottom: none;
}

.activity-icon {
  min-width: 24px;
  text-align: center;
}

.activity-title {
  font-weight: 600;
  color: var(--bs-body-color);
  margin: 0;
}

.activity-time {
  font-size: 0.75rem;
  white-space: nowrap;
}

.activity-meta .badge {
  font-size: 0.7rem;
}

@media (max-width: 768px) {
  .activity-item {
    padding-left: 0;
    padding-right: 0;
  }

  .activity-meta {
    flex-wrap: wrap;
    gap: 0.25rem;
  }
}
</style>
